import {
  CarrierType,
  CarrierVisitDirection,
  RailVisitResponseDto,
  VesselVisitDto,
} from '@planning/app/api'
import { railVisitService, vesselVisitService } from '@planning/services'
import { FieldStore, ISubFormStore, required, validateSubForm } from '@tom-ui/utils'
import { action, computed, makeObservable, observable, runInAction } from 'mobx'

export type ChangeTransportSubFormType = {
  carrierName?: string
  carrierType?: CarrierType
  carrierVisitId?: number
  railTrackId?: string
  railcarId?: number
  releaseExpires?: string
  eta?: string
  bay?: string
  row?: string
  tier?: string
  isFinalPosition?: boolean
  orderId?: number
}

export class ChangeTransportSubFormStore implements ISubFormStore<ChangeTransportSubFormType> {
  carrierType: FieldStore<string>
  carrierVisitId: FieldStore<number>
  railTrackId: FieldStore<string>
  railcarId: FieldStore<number | undefined>
  releaseExpires: FieldStore<string>
  bay: FieldStore<string>
  row: FieldStore<string>
  tier: FieldStore<string>
  isFinalPosition = false
  orderId?: number

  showDatetimeForPickUp = false
  selectedRailVisit?: RailVisitResponseDto
  selectedVesselVisit?: VesselVisitDto

  containerNumber?: string | null
  direction?: CarrierVisitDirection

  constructor(initialValues?: Partial<ChangeTransportSubFormType>) {
    makeObservable(this, {
      showDatetimeForPickUp: observable,
      selectedRailVisit: observable,
      selectedVesselVisit: observable,
      containerNumber: observable,
      direction: observable,
      hasDuplicateContainerConflict: computed,
      setCarrierType: action,
      setShowDatetimeForPickUp: action,
      setSelectedRailVisit: action,
      setSelectedVesselVisit: action,
      setContainerNumberAndDirection: action,
    })

    this.carrierType = new FieldStore<string>(initialValues?.carrierType ?? '')
    this.carrierVisitId = new FieldStore<number>(initialValues?.carrierVisitId ?? 0, required())
    this.railTrackId = new FieldStore<string>(initialValues?.railTrackId ?? '', required())
    this.railcarId = new FieldStore<number | undefined>(initialValues?.railcarId, required())
    this.releaseExpires = new FieldStore<string>(initialValues?.releaseExpires ?? '')
    if (this.releaseExpires.value) {
      this.setShowDatetimeForPickUp(true)
    }

    this.bay = new FieldStore<string>(initialValues?.bay ?? '', this.stowagePositionValidation())
    this.row = new FieldStore<string>(initialValues?.row ?? '', this.stowagePositionValidation())
    this.tier = new FieldStore<string>(initialValues?.tier ?? '', this.stowagePositionValidation())
    this.isFinalPosition = initialValues?.isFinalPosition ?? false
    this.orderId = initialValues?.orderId

    this.carrierType.touch()
    this.bay.touch()
    this.row.touch()
    this.tier.touch()

    if (initialValues?.carrierType === CarrierType.Train && initialValues?.carrierVisitId) {
      this.loadRailVisit(initialValues.carrierVisitId)
    }

    if (initialValues?.carrierType === CarrierType.Vessel && initialValues?.carrierVisitId) {
      this.loadVesselVisit(initialValues.carrierVisitId)
    }
  }

  loadRailVisit = async (visitId: number) => {
    const visit = await railVisitService.getById(visitId)
    runInAction(() => {
      this.selectedRailVisit = visit
      this.carrierVisitId.touch()
      this.railTrackId.touch()
      this.railcarId.touch()
    })
  }

  loadVesselVisit = async (visitId: number) => {
    const visit = await vesselVisitService.getById(visitId)
    runInAction(() => {
      this.selectedVesselVisit = visit
    })
  }

  validate = () => {
    const result = validateSubForm(this, true)
    return result
  }

  setCarrierType = (value: string) => {
    this.carrierVisitId.clear()
    this.railTrackId.clear()
    this.railcarId.clear()
    this.selectedVesselVisit = undefined
    this.selectedRailVisit = undefined
    this.bay.clear()
    this.row.clear()
    this.tier.clear()
    this.carrierType.setValue(value)

    switch (value) {
      case CarrierType.Train:
        this.carrierVisitId.touch()
        this.railTrackId.touch()
        this.railcarId.touch()
        break
      case CarrierType.Vessel:
        this.carrierVisitId.touch()
        break
      case CarrierType.Truck:
        this.carrierVisitId.setValue(0)
        break
    }
  }

  setShowDatetimeForPickUp = (value: boolean) => {
    this.showDatetimeForPickUp = value
    if (value) {
      this.releaseExpires.touch()
    } else {
      this.releaseExpires.unTouch()
    }
  }

  setSelectedRailVisit = (value: RailVisitResponseDto | undefined) => {
    this.selectedRailVisit = value
  }

  setSelectedVesselVisit = (value: VesselVisitDto | undefined) => {
    this.selectedVesselVisit = value
  }

  setContainerNumberAndDirection = (
    containerNumber: string | null | undefined,
    direction: CarrierVisitDirection,
  ) => {
    this.containerNumber = containerNumber
    this.direction = direction
  }

  get hasDuplicateContainerConflict(): boolean {
    const carrierVisit = this.selectedVesselVisit ?? this.selectedRailVisit
    if (!carrierVisit || !this.containerNumber) return false

    const containerMap =
      this.direction === CarrierVisitDirection.Inbound
        ? carrierVisit.inboundOrderIdContainerNumbers
        : carrierVisit.outboundOrderIdContainerNumbers

    if (!containerMap) return false

    const currentOrderId = this.orderId?.toString()

    return Object.entries(containerMap).some(([orderId, containerNumbers]) => {
      if (orderId === currentOrderId) return false
      return containerNumbers.includes(this.containerNumber!)
    })
  }

  get data() {
    switch (this.carrierType.value) {
      case CarrierType.Vessel:
        return {
          carrierName: this.selectedVesselVisit?.identifier ?? '-',
          carrierType: this.carrierType.value as CarrierType,
          carrierVisitId: this.carrierVisitId.value,
          eta: this.selectedVesselVisit?.eta,
          bay: this.bay.value,
          row: this.row.value,
          tier: this.tier.value,
          isFinalPosition: this.isFinalPosition,
        }
      case CarrierType.Train:
        return {
          carrierName: this.selectedRailVisit?.identifier ?? '-',
          carrierType: this.carrierType.value as CarrierType,
          carrierVisitId: this.carrierVisitId.value,
          railTrackId: this.railTrackId.value,
          railcarId: this.railcarId.value ?? undefined,
          eta: this.selectedRailVisit?.eta,
        }
      case CarrierType.Truck:
        return {
          carrierName: this.selectedRailVisit?.identifier ?? '-',
          carrierVisitId: this.carrierVisitId.value,
          carrierType: this.carrierType.value as CarrierType,
          releaseExpires: this.releaseExpires.value,
        }
    }
    return {
      carrierType: this.carrierType.value as CarrierType,
      carrierVisitId: this.carrierVisitId.value,
      railTrackId: this.railTrackId.value,
      railcarId: this.railcarId.value,
      releaseExpires: this.releaseExpires.value,
    }
  }

  stowagePositionValidation =
    () =>
    <T>(val: T) => {
      const hasBaroti = this.bay?.value || this.row?.value || this.tier?.value
      return hasBaroti && (val === null || val === 0 || val === '' || val === undefined)
        ? 'fieldIsRequired'
        : undefined
    }
}
